import React, { useState } from 'react';
import {
  EDIT_SIZE_ORDER,
  SIZE_LABELS,
  serviceFlags,
  priceText,
} from './catalogValidation';
import { needDataPill } from './ServiceCatalogGrid';
import { formulaText, formulaVars } from '../../../util/areaFormulaDisplay';
import { AREA_VAR_LABELS } from '../../../util/marineCalc';
import './serviceCatalog.css';

/**
 * ServiceEditView — redesigned service-edit page (mockup admin-03 / Review 3a).
 *
 * Presentational: reads the *normalized* service (null-aware prices) so the base
 * table flags missing (——) vs zero ($0.00) correctly, and delegates all mutations
 * to the container's existing handlers/modals.
 *
 * Excluded per scope: the "Dealer pricing overrides" section (new cascade
 * architecture) — a code seam is left where it would mount.
 *
 * Props:
 *   svc            normalized service { id, name, cov, categoryId, categoryName, p, d, q, dur }
 *   catalog        Array normalized categories (for the switch-service selects)
 *   canEdit        bool
 *   unitLabel      string  expected-qty unit (e.g. "sq ft")
 *   notes          string
 *   allowMultiple, taxable  bool  (header badges)
 *   trimOverrideCount, vehicleOverrides, productItemCount, workflowStepCount
 *   areaConfig     object|null  extracted pricing config (Task 7); when
 *                  areaConfig.isArea is true, replaces the size table with the
 *                  Measured panel and "Measured" badge; when areaConfig.isFlat
 *                  is true, replaces it with the Flat panel and "Flat" badge
 *   bodyStyles     string[]  body-style restriction on an automotive size-priced
 *                  service; [] = all body styles. Not shown for Measured, Flat or marine.
 *   onBack, onOpenService(id), onManageVehicle, onManageProducts, onManageWorkflow,
 *   onUpdate, onDelete
 */
export default function ServiceEditView({
  svc,
  catalog = [],
  canEdit = false,
  readOnly = false,
  unitLabel = '',
  notes = '',
  allowMultiple = false,
  taxable = true,
  trimOverrideCount = 0,
  vehicleOverrides = [],
  productItemCount = 0,
  workflowStepCount = 0,
  areaConfig = null,
  bodyStyles = [],
  onBack,
  onOpenService,
  onManageVehicle,
  onManageProducts,
  onManageWorkflow,
  onUpdate,
  onDelete,
}) {
  const [switchCatId, setSwitchCatId] = useState(svc ? svc.categoryId : null);

  if (!svc) return null;

  // Custom services are priced on the estimate and are never editable here.
  const editable = canEdit && !readOnly;
  const isArea = !!(areaConfig && areaConfig.isArea);
  const isFlat = !!(areaConfig && areaConfig.isFlat);
  const flags = serviceFlags(svc);
  const switchCat = catalog.find((c) => c.id === switchCatId) || catalog.find((c) => c.id === svc.categoryId);
  const switchServices = switchCat ? switchCat.services : [];

  return (
    <React.Fragment>
      {/* Breadcrumb */}
      <div className="jv-card p-3 mb-3">
        <div className="sc-breadcrumb">
          <span className="sc-back" onClick={onBack}>
            <i className="fa-sharp fa-regular fa-arrow-left" /> All services
          </span>
          <span className="sc-crumb-divider" />
          <span className="jv-page-title" style={{ marginBottom: 0 }}>{svc.name}</span>
          {svc.isCustom && <span className="jv-badge jv-badge-neutral"><i className="fa-sharp fa-regular fa-lock" /> Custom</span>}
          {svc.cov && <span className="jv-badge jv-badge-neutral">{svc.cov}</span>}
          {isArea && <span className="jv-badge jv-badge-neutral">Measured</span>}
          {isFlat && <span className="jv-badge jv-badge-neutral">Flat</span>}
          {allowMultiple && <span className="jv-badge jv-badge-success">Multi-Qty</span>}
          {!taxable && <span className="jv-badge jv-badge-warning">Non-Taxable</span>}
          <span style={{ marginLeft: 'auto' }} />
          {needDataPill(flags.err + flags.warn)}
        </div>
        {svc.isCustom
          ? <small className="text-muted d-block mt-2"><i className="fa-sharp fa-regular fa-circle-info" /> Custom service — priced on the estimate. Base price and duration are always $0 and can't be edited here.</small>
          : (notes ? <small className="text-muted d-block mt-2">{notes}</small> : null)}
        {/* Body-style restriction — automotive size-priced services only. A boat has
            no body style, and Measured/Flat pricing has no body-style axis. */}
        {!isArea && !isFlat && areaConfig?.assetType !== 'marine' && (
          <small className="text-muted d-block mt-1">
            <i className="fa-sharp fa-regular fa-car" /> Applies to:{' '}
            {Array.isArray(bodyStyles) && bodyStyles.length > 0 ? bodyStyles.join(', ') : 'All body styles'}
          </small>
        )}
      </div>

      {/* Main card */}
      <div className="jv-card">
        {/* header strip: category · base pricing + switch-service selects */}
        <div className="jv-card-header" style={{ background: 'var(--color-bg-panel)' }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 'var(--space-3)' }}>
            <span className="jv-card-title">{svc.categoryName} · base pricing</span>
            <div className="d-flex align-items-center" style={{ gap: 'var(--space-2)' }}>
              <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>Switch service</span>
              <select
                className="form-control form-control-sm"
                style={{ width: 'auto' }}
                value={switchCat ? switchCat.id : ''}
                onChange={(e) => setSwitchCatId(Number(e.target.value))}
              >
                {catalog.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select
                className="form-control form-control-sm"
                style={{ width: 'auto', borderColor: 'var(--color-brand-teal)' }}
                value={svc.id}
                onChange={(e) => { const id = Number(e.target.value); if (id !== svc.id) onOpenService(id); }}
              >
                {switchServices.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="card-body">
          {/* Base pricing table (XL → L → M → S), or the area-priced panel */}
          {isArea ? (
            <AreaPricingPanel cfg={areaConfig} />
          ) : isFlat ? (
            <FlatPricingPanel cfg={areaConfig} />
          ) : (
            <div className="table-responsive">
              <table className="jv-table jv-table-sm mb-0">
                <thead>
                  <tr>
                    <th>Size</th>
                    <th className="text-right">Retail price</th>
                    <th className="text-right">Wholesale price</th>
                    <th className="text-right">Duration</th>
                    <th className="text-right">Expected qty</th>
                  </tr>
                </thead>
                <tbody>
                  {EDIT_SIZE_ORDER.map((size) => {
                    const [r, w] = svc.p[size];
                    const inverted = r != null && w != null && r !== 0 && w !== 0 && r < w;
                    const bad = (v) => v === null || v === undefined || v === 0;
                    const dur = svc.d[size];
                    const qty = svc.q[size];
                    return (
                      <tr key={size}>
                        <td><strong>{SIZE_LABELS[size]}</strong></td>
                        <td className={`text-right sc-edit-price ${bad(r) ? 'is-error' : inverted ? 'is-warn' : ''}`}>{priceText(r)}</td>
                        <td className={`text-right sc-edit-price sc-edit-price--whole ${bad(w) ? 'is-error' : ''}`}>{priceText(w)}</td>
                        <td className="text-right sc-edit-price sc-edit-price--whole">{dur == null ? '——' : `${dur} days`}</td>
                        <td className="text-right sc-edit-price sc-edit-price--whole">{qty == null ? '—' : `${qty}${unitLabel ? ' ' + unitLabel : ''}`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* 1. Vehicle-specific pricing — size-priced services only. An area
              (Measured) service is priced by the square foot off the asset's
              own dimensions, and a Flat service has one fixed SF price, so
              neither has a per-vehicle price to override: the overrides are
              keyed on trim + vehicle size, and both have neither (their single
              price row is vehicle_size 'SF'). */}
          {!isArea && !isFlat && (
            <Section
              icon="fa-car"
              title="Vehicle-specific pricing"
              desc="Custom prices for specific vehicles (make / model / trim), applied instead of the base size price."
              countText={trimOverrideCount > 0 ? `${trimOverrideCount} vehicle${trimOverrideCount === 1 ? '' : 's'} with custom pricing` : 'No custom vehicle pricing set'}
              canEdit={editable}
              onAdd={onManageVehicle}
              addLabel={trimOverrideCount > 0 ? 'Manage' : 'Add'}
            >
              {vehicleOverrides.length > 0 && (
                <div className="table-responsive" style={{ maxHeight: 200, overflowY: 'auto' }}>
                  <table className="jv-table jv-table-sm mb-0">
                    <thead>
                      <tr><th>Vehicle</th><th className="text-right">Retail</th><th className="text-right">Wholesale</th></tr>
                    </thead>
                    <tbody>
                      {vehicleOverrides.map((v, idx) => (
                        <tr key={idx}>
                          <td>
                            <strong>{v.make}</strong> {v.model}
                            {v.trim && v.trim !== 'Unknown' && <small className="text-muted ml-1">({v.trim})</small>}
                            {v.vehicleSize && <span className="jv-badge jv-badge-neutral ml-1" style={{ fontSize: 10 }}>{v.vehicleSize}</span>}
                          </td>
                          <td className="text-right">{v.retailPrice ? `$${v.retailPrice.toFixed(2)}` : '-'}</td>
                          <td className="text-right">{v.wholesalePrice ? `$${v.wholesalePrice.toFixed(2)}` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          )}

          {/* 2. Dealer pricing overrides — OMITTED (out of scope: dealership→group→base cascade).
              Mount point left here intentionally for a future phase. */}

          {/* 3. Preferred product(s) */}
          <Section
            icon="fa-cube"
            title="Preferred product(s)"
            desc="Products captured against this service for material tracking."
            countText={productItemCount > 0 ? `${productItemCount} preferred product${productItemCount === 1 ? '' : 's'} defined` : 'No preferred products defined'}
            canEdit={editable}
            onAdd={onManageProducts}
            addLabel={productItemCount > 0 ? 'Manage' : 'Add'}
          />

          {/* 4. Workflow steps */}
          <Section
            icon="fa-list-check"
            title="Workflow steps"
            desc="Work steps a technician completes for this service."
            countText={workflowStepCount > 0 ? `${workflowStepCount} workflow step${workflowStepCount === 1 ? '' : 's'} defined` : 'No workflow steps defined'}
            canEdit={editable}
            onAdd={onManageWorkflow}
            addLabel={workflowStepCount > 0 ? 'Manage' : 'Add'}
          />
        </div>
      </div>

      {/* Footer */}
      {editable && (
        <div className="col text-center mt-3">
          <button type="button" className="jv-btn jv-btn-primary m-2" onClick={onUpdate}>Update Service</button>
          <button type="button" className="jv-btn jv-btn-danger m-2" onClick={onDelete}>Delete Service</button>
        </div>
      )}
    </React.Fragment>
  );
}

function AreaPricingPanel({ cfg }) {
  const money = (v) => (v === '' || v == null ? '——' : `$${Number(v).toFixed(2)}`);
  const unitLabel = cfg.unitOfMeasure === 'ft' ? 'ft' : cfg.unitOfMeasure === 'each' ? 'each' : 'sq ft';
  const vars = formulaVars(cfg.areaFormula);
  const text = formulaText(cfg.areaFormula, AREA_VAR_LABELS);
  return (
    <React.Fragment>
      <div className="table-responsive">
        <table className="jv-table jv-table-sm mb-0">
          <thead>
            <tr>
              <th>Retail rate</th>
              <th className="text-right">Wholesale rate</th>
              <th className="text-right">Factor</th>
              <th className="text-right">Waste</th>
              <th className="text-right">Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>{money(cfg.retailRate)}</strong> <small className="text-muted">/{unitLabel}</small></td>
              <td className="text-right">{money(cfg.wholesaleRate)} <small className="text-muted">/{unitLabel}</small></td>
              <td className="text-right">{cfg.areaFactor === '' ? '——' : Number(cfg.areaFactor)}</td>
              <td className="text-right">{cfg.wastePct === '' ? '——' : `${Number(cfg.wastePct)}%`}</td>
              <td className="text-right">{cfg.durationDays === '' ? '——' : `${cfg.durationDays} days`}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {text && (
        <div className="sc-edit-section">
          <div className="sc-edit-section__title">
            <i className="fa-sharp fa-regular fa-function text-muted" /> Area formula
          </div>
          <div className="text-monospace small">{text}</div>
          {vars.length > 0 && (
            <div className="sc-edit-section__desc">
              Asks the estimator for: {vars.map((v) => AREA_VAR_LABELS[v] || v).join(', ')}
            </div>
          )}
          <div className="sc-edit-section__desc">
            Authored by this shop. Edit the service to change it.
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

function FlatPricingPanel({ cfg }) {
  const money = (v) => (v === '' || v == null ? '——' : `$${Number(v).toFixed(2)}`);
  return (
    <div className="table-responsive">
      <table className="jv-table jv-table-sm mb-0">
        <thead>
          <tr>
            <th>Retail price</th>
            <th className="text-right">Wholesale price</th>
            <th className="text-right">Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>{money(cfg.retailRate)}</strong></td>
            <td className="text-right">{money(cfg.wholesaleRate)}</td>
            <td className="text-right">{cfg.durationDays === '' ? '——' : `${cfg.durationDays} days`}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Section({ icon, title, desc, countText, canEdit, onAdd, addLabel, children }) {
  return (
    <div className="sc-edit-section">
      <div className="sc-edit-section__head">
        <div>
          <div className="sc-edit-section__title">
            <i className={`fa-sharp fa-regular ${icon} text-muted`} /> {title}
          </div>
          <div className="sc-edit-section__desc">{desc}</div>
          <div className="sc-edit-section__desc">{countText}</div>
        </div>
        {canEdit && (
          <button type="button" className="sc-btn-add" onClick={onAdd}>
            <i className="fa-sharp fa-solid fa-plus" /> {addLabel}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
